/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseHeader
 *
 * @file CourseHeader.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@mui/material';
import { BUTTON_TYPES } from '../../common/constants';
import Icon from '../icons/Icon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Download from '../download/Download';
import {
  CourseHeaderBookCard,
  CourseHeaderBookTitle,
  CourseHeaderTitleInfoContainer,
  CourseHeaderIconButton,
  CourseHeaderHotspot,
  CourseHeaderCoverImageContainer,
  CourseHeaderCoverImage,
  CourseHeaderDownloadRoot,
  CourseHeaderAuthor,
  CourseHeaderBookInfo,
  CourseHeaderSecondaryButton,
  CourseHeaderGoToPageContainer,
  CourseHeaderGoToPageLabel,
  CourseHeaderGoToTextField,
  CourseHeaderActionsContainer,
  CourseHeaderBookActionsContainer,
  CourseHeaderBookActions,
  CourseHeaderSecondaryGuidesButton
} from './styles/CourseHeaderStyles';

const EtextCard = (props) => {
  const isScreenBelow768 = !useMediaQuery('@media screen and (min-width: 768px)');

  const {
    book = {},
    config = {}
  } = props;

  const {
    showGoToPage = true,
    showBookActions = true,
    showBookTitle = true,
    showAuthor = true,
    showBookCover = true,
    showSccessEducatorGuides = true,
    showTitleInfo = true,
    showDownload = true
  } = config;

  const renderTitleInfo = () => {
    if (!showTitleInfo) {
      return null;
    }

    const { tooltip, callback, showHotspot = false } = book.titleInfo || {};

    return (
      <TooltipWrapper tooltip={tooltip || ''} placement="top">
        <CourseHeaderIconButton
          onClick={() => {
            callback?.();
          }}
          aria-haspopup="dialog"
        >
          {showHotspot && <CourseHeaderHotspot />}
          <Icon name="InfoIcon" />
        </CourseHeaderIconButton>
      </TooltipWrapper>
    );
  };

  const renderTitle = () => {
    if (!showBookTitle || !book.title) {
      return null;
    }

    return (
      <CourseHeaderTitleInfoContainer>
        <CourseHeaderBookTitle>
          {book.title}
        </CourseHeaderBookTitle>
        {renderTitleInfo()}
      </CourseHeaderTitleInfoContainer>
    );
  };

  const renderBookCover = () => {
    if (!showBookCover || !book.coverImage) {
      return null;
    }

    const { progress, status, onCancelClick, onBookClick } = book?.titleInfo || {};

    return (
      <CourseHeaderCoverImageContainer>
        {showDownload ?
          <CourseHeaderDownloadRoot>
            <Download
              backgroundImage={book?.coverImage}
              progress={progress}
              downloadStatus={status}
              onCancelClick={onCancelClick}
              onClick={onBookClick}
            />
          </CourseHeaderDownloadRoot>
          : (
            <CourseHeaderCoverImage
              src={book.coverImage}
              alt={book.title ? `Book cover for ${book.title}` : 'Book cover'}
            />
          )
        }
      </CourseHeaderCoverImageContainer>
    );
  };

  const renderAuthor = () => {
    if (!showAuthor || !book.author) {
      return null;
    }

    return (
      <CourseHeaderAuthor>
        {book.author}
      </CourseHeaderAuthor>
    );
  };

  const renderTextButton = (action) => {
    if (!action.text) {
      return null;
    }

    return (
      <CourseHeaderSecondaryButton
        onClick={action.callback}
      >
        {action.text}
      </CourseHeaderSecondaryButton>
    );
  };

  const renderGoToPage = () => {
    if (!showGoToPage || !book.goToPage) return null;

    const {
      label,
      placeholder,
      value,
      maxLength,
      callback
    } = book.goToPage || {};

    return (
      <CourseHeaderGoToPageContainer>
        {label && <CourseHeaderGoToPageLabel>{label}</CourseHeaderGoToPageLabel>}
        <CourseHeaderGoToTextField
          variant="standard"
          value={value}
          placeholder={placeholder}
          onChange={(event) => callback(event?.target?.value)}
          InputProps={{
            disableUnderline: true,
            inputProps: {
              maxLength,
              'aria-label': 'Go to page'
            }
          }}
        />
      </CourseHeaderGoToPageContainer>
    );
  };

  const renderAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return renderTextButton(action);
    }

    return null;
  };

  const renderActions = () => {
    if (!showBookActions || !book.actions) {
      return null;
    }

    return (
      <CourseHeaderActionsContainer>
        {book.actions.map((action) => renderAction(action))}
      </CourseHeaderActionsContainer>
    );
  };

  const renderAccessEducatorGuides = () => {
    if (!showSccessEducatorGuides || !book.accessEducatorGuides) return null;

    const { text, callback } = book.accessEducatorGuides || {};

    return (
      <CourseHeaderSecondaryGuidesButton
        onClick={callback}
      >
        {text}
      </CourseHeaderSecondaryGuidesButton>
    );
  };

  return (
    <Fragment>
      <CourseHeaderBookCard>
        {isScreenBelow768 ? null : renderBookCover()}
        <CourseHeaderBookInfo>
          {renderTitle()}
          {renderAuthor()}
          <CourseHeaderBookActionsContainer>
            {isScreenBelow768 ? renderBookCover() : null}
            <CourseHeaderBookActions>
              {renderGoToPage()}
              {renderActions()}
              {renderAccessEducatorGuides()}
            </CourseHeaderBookActions>
          </CourseHeaderBookActionsContainer>
        </CourseHeaderBookInfo>
      </CourseHeaderBookCard>
    </Fragment>
  );
};

EtextCard.propTypes = {
  book: PropTypes.object,
  config: PropTypes.object
};

EtextCard.defaultProps = {
  book: {},
  config: {}
};

export default EtextCard;
